import { nightCount, fmtF, curSym } from './utils.js';

export const DEFAULT_TEMPLATES = {
  confirmation: {
    en: `Dear [guestName],

Your booking at [hotelName] is confirmed:
Check-in: [checkIn]
Check-out: [checkOut]
[nights] nights — €[totalPrice]

[breakfast]

See you then!
[hotelName]`,
    bg: `Здравейте [guestName],

Вашата резервация в [hotelName] е потвърдена:
Настаняване: [checkIn]
Напускане: [checkOut]
[nights] нощувки, €[totalPrice]

[breakfast]

До скоро!
[hotelName]`,
  },

  confirmation_bcom_suffix: {
    en: `\nFor the best guaranteed price and no fees, book directly with us next time: [directBookingUrl]`,
    bg: `\nЗа най-добра цена без такси, следващия път резервирайте директно при нас: [directBookingUrl]`,
  },

  deposit_request: {
    en: `Dear [guestName],

To secure your booking ([checkIn] – [checkOut]), please pay a deposit of €[depositAmount]:

[paymentInstructions]

Reference: [guestName] [checkIn]

We'll confirm once received.
[hotelName]`,
    bg: `Здравейте [guestName],

За да потвърдим Вашата резервация ([checkIn] – [checkOut]), моля, заплатете депозит от €[depositAmount]:

[paymentInstructions]

Основание: [guestName] [checkIn]

Ще потвърдим при получаване.
[hotelName]`,
  },

  balance_reminder: {
    en: `Dear [guestName],

We've received your deposit of €[depositAmount] — thank you.

Your remaining balance of €[balanceDue] is due on arrival.

Looking forward to welcoming you on [checkIn]!
[hotelName]`,
    bg: `Здравейте [guestName],

Получихме Вашия депозит от €[depositAmount]. Благодарим!

Оставащият баланс от €[balanceDue] се заплаща при пристигане.

Очакваме Ви на [checkIn]!
[hotelName]`,
  },

  arrival_reminder: {
    en: `Dear [guestName],

We're looking forward to welcoming you tomorrow!

Address: [hotelAddress]
Check-in from: [checkInTime]

[directions]

[breakfast]

See you on [checkIn]!
[hotelName]`,
    bg: `Здравейте [guestName],

Очакваме Ви утре!

Адрес: [hotelAddress]
Настаняване от: [checkInTime]

[directions]

[breakfast]

До скоро на [checkIn]!
[hotelName]`,
  },

  review_request: {
    en: `Dear [guestName],

Thank you for staying with us at [hotelName]!
We hope you had a wonderful time.

If you'd like to leave us a review, we'd really appreciate it:
[googleReviewLink]

Hope to see you again next season!
[hotelName]`,
    bg: `Здравейте [guestName],

Благодарим, че отседнахте при нас в [hotelName]!
Надяваме се да сте прекарали чудесно.

Ако желаете да ни оставите отзив, ще ни бъде много приятно:
[googleReviewLink]

Очакваме Ви отново следващия сезон!
[hotelName]`,
  },
};

export function interpolateTemplate(template, vars) {
  const interpolated = template.replace(/\[(\w+)\]/g, (match, key) => {
    return key in vars ? String(vars[key]) : match;
  });
  return interpolated.replace(/\n{3,}/g, '\n\n');
}

export function getTemplateText(property, templateKey, lang) {
  const custom = property?.message_templates?.[templateKey];
  if (custom && custom[lang]) return custom[lang];
  return DEFAULT_TEMPLATES[templateKey]?.[lang] ?? '';
}

export function buildTemplateVars(booking, room, property, lang = 'en', extras = {}) {
  const nights = nightCount(booking.checkIn, booking.checkOut);
  const total = Number(booking.price) || 0;
  const deposit = Number(booking.depositAmount) || 0;
  const balance = Math.max(0, total - deposit);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const rawSortCode = property?.bank_uk_sort_code ?? '';
  const formattedSortCode = /^\d{6}$/.test(rawSortCode)
    ? `${rawSortCode.slice(0, 2)}-${rawSortCode.slice(2, 4)}-${rawSortCode.slice(4, 6)}`
    : rawSortCode;
  const maps = property?.google_maps_link ?? '';
  const waze = property?.waze_link ?? '';
  const directionsLines = [];
  if (maps) directionsLines.push(`Google Maps: ${maps}`);
  if (waze) directionsLines.push(`Waze: ${waze}`);
  const directions = directionsLines.join('\n');
  const stripeEnabled = !!property?.stripe_restricted_key;
  const paymentLink = typeof extras.paymentLink === 'string' ? extras.paymentLink : '';
  const bankDetails = renderBankDetails(property, formattedSortCode);
  const breakfastIncluded = !!booking.breakfastIncluded;
  const breakfastSnapPrice = Number(booking.breakfastPrice) || 0;
  const cur = curSym(property?.currency);
  let breakfast = '';
  let breakfastNote = '';
  let breakfastPriceStr = '';
  if (breakfastIncluded) {
    if (lang === 'bg') {
      breakfast = breakfastSnapPrice > 0
        ? `Закуска: ${cur}${breakfastSnapPrice} (включена в общата сума)`
        : 'Закуска: включена';
      breakfastNote = 'със закуска';
    } else {
      breakfast = breakfastSnapPrice > 0
        ? `Breakfast: ${cur}${breakfastSnapPrice} (included in total)`
        : 'Breakfast: included';
      breakfastNote = 'with breakfast';
    }
    breakfastPriceStr = breakfastSnapPrice > 0 ? `${cur}${breakfastSnapPrice}` : '';
  }
  return {
    guestName:        booking.guestName,
    hotelName:        property?.name ?? '',
    roomName:         room?.name ?? '',
    checkIn:          fmtF(booking.checkIn),
    checkOut:         fmtF(booking.checkOut),
    nights,
    totalPrice:       total,
    depositAmount:    deposit || '',
    balanceDue:       String(balance),
    bankName:         property?.bank_name ?? '',
    iban:             property?.bank_iban ?? '',
    accountHolder:    property?.bank_account_holder ?? '',
    bic:              property?.bank_bic ?? '',
    accountNumber:    property?.bank_uk_account_number ?? '',
    sortCode:         formattedSortCode,
    hotelAddress:     property?.address ?? '',
    checkInTime:      property?.check_in_time ?? '',
    googleReviewLink: property?.google_review_link ?? '',
    googleMapsLink:   property?.google_maps_link ?? '',
    wazeLink:         property?.waze_link ?? '',
    directions,
    bankDetails,
    paymentInstructions: renderPaymentInstructions({
      stripeEnabled,
      paymentLink,
      bankDetails,
      lang,
    }),
    directBookingUrl: property?.slug ? `${baseUrl}/book/${property.slug}` : '',
    breakfast,
    breakfastNote,
    breakfastPrice: breakfastPriceStr,
  };
}

function renderBankDetails(property, formattedSortCode) {
  if (!property) return '';
  const country = property.country ?? 'GR';
  const holder = property.bank_account_holder ?? '';
  const bankName = property.bank_name ?? '';
  const iban = property.bank_iban ?? '';
  const bic = property.bank_bic ?? '';
  const ukAccount = property.bank_uk_account_number ?? '';

  function buildInternational() {
    if (!iban) return '';
    const lines = [];
    if (holder) lines.push(holder);
    if (bankName) lines.push(bankName);
    lines.push('IBAN: ' + iban);
    if (bic) lines.push('BIC: ' + bic);
    return lines.join('\n');
  }

  function buildUkDomestic() {
    if (!ukAccount) return '';
    const lines = [];
    if (holder) lines.push(holder);
    lines.push('Account: ' + ukAccount);
    if (formattedSortCode) lines.push('Sort code: ' + formattedSortCode);
    return lines.join('\n');
  }

  if (country === 'GB') {
    const ukBlock = buildUkDomestic();
    const intlBlock = buildInternational();
    const parts = [];
    if (ukBlock) parts.push('UK (domestic):\n' + ukBlock);
    if (intlBlock) parts.push('International:\n' + intlBlock);
    return parts.join('\n\n');
  }

  return buildInternational();
}

function renderPaymentInstructions({ stripeEnabled, paymentLink, bankDetails, lang }) {
  if (stripeEnabled) {
    if (paymentLink) {
      return lang === 'bg'
        ? `Платете сигурно тук:\n${paymentLink}`
        : `Pay securely here:\n${paymentLink}`;
    }
    return lang === 'bg'
      ? '[Сигурен линк за плащане ще бъде добавен при изпращане]'
      : '[Secure payment link will be inserted when you send]';
  }
  return bankDetails;
}

